/**
 * Network Monitor
 * 
 * Handles real-time connection monitoring and state detection.
 * Provides connection type detection, connection change events,
 * network availability assessment, and reconnection logic.
 */

import { Network, ConnectionStatus, ConnectionType } from '@capacitor/network';
import { NetworkEvent, NetworkEventListener } from './BaseNetworkService';

/**
 * Network Monitor
 * 
 * Responsible for monitoring network connection state and detecting changes.
 * Emits events when network status changes and manages connection timing.
 */
export class NetworkMonitor {
  private eventListeners: Set<NetworkEventListener> = new Set();
  private currentStatus: ConnectionStatus | null = null;
  private isMonitoring = false;
  private enableDebugLogging: boolean;

  constructor(enableDebugLogging = true) {
    this.enableDebugLogging = enableDebugLogging;
  }

  /**
   * Start monitoring network status
   */
  async startMonitoring(): Promise<void> {
    if (this.isMonitoring) return;

    try {
      this.debugLog('🌐 Starting network monitoring...');

      // Get initial network status
      this.currentStatus = await Network.getStatus();
      
      this.debugLog('🌐 Initial network status:', this.currentStatus);

      // Set up network status listener
      Network.addListener('networkStatusChange', (status) => {
        this.handleNetworkStatusChange(status);
      });

      this.isMonitoring = true;
      this.debugLog('✅ Network monitoring started successfully');
    } catch (error) {
      console.error('❌ Failed to start network monitoring:', error);
      throw error;
    }
  }

  /**
   * Stop monitoring network status
   */
  stopMonitoring(): void {
    if (!this.isMonitoring) return;

    try {
      // Remove all network listeners
      Network.removeAllListeners();
      this.isMonitoring = false;
      
      this.debugLog('🛑 Network monitoring stopped');
    } catch (error) {
      console.error('❌ Failed to stop network monitoring:', error);
    }
  }

  /**
   * Add event listener
   */
  addEventListener(listener: NetworkEventListener): void {
    this.eventListeners.add(listener);
  }

  /**
   * Remove event listener
   */
  removeEventListener(listener: NetworkEventListener): void {
    this.eventListeners.delete(listener);
  }

  /**
   * Remove all event listeners
   */
  removeAllEventListeners(): void {
    this.eventListeners.clear();
  }

  /**
   * Get current network status
   */
  getCurrentStatus(): ConnectionStatus | null {
    return this.currentStatus;
  }

  /**
   * Check if currently connected
   */
  isConnected(): boolean {
    return this.currentStatus?.connected || false;
  }

  /**
   * Get current connection type
   */
  getConnectionType(): ConnectionType | null {
    return this.currentStatus?.connectionType || null;
  }

  /**
   * Handle network status change
   */
  private handleNetworkStatusChange(newStatus: ConnectionStatus): void {
    const previousStatus = this.currentStatus;
    const timestamp = new Date().toISOString();
    
    this.debugLog('🌐 Network status changed:', {
      from: previousStatus,
      to: newStatus,
      timestamp
    });

    // Handle connection state changes
    if (previousStatus && !previousStatus.connected && newStatus.connected) {
      // Network restored
      this.handleNetworkRestored(newStatus, timestamp);
    } else if (previousStatus && previousStatus.connected && !newStatus.connected) {
      // Network lost
      this.handleNetworkLost(previousStatus, timestamp);
    } else if (previousStatus && previousStatus.connected && newStatus.connected) {
      // Connection type changed
      if (previousStatus.connectionType !== newStatus.connectionType) {
        this.handleConnectionTypeChanged(previousStatus, newStatus, timestamp);
      }
    }

    // Update current status
    this.currentStatus = newStatus;
  }

  /**
   * Handle network restored
   */
  private handleNetworkRestored(status: ConnectionStatus, timestamp: string): void {
    this.debugLog('🟢 Network restored:', { connectionType: status.connectionType, timestamp });

    this.emitEvent({
      type: 'network_connected',
      data: {
        connectionType: status.connectionType,
        timestamp
      }
    });
  }

  /**
   * Handle network lost
   */
  private handleNetworkLost(previousStatus: ConnectionStatus, timestamp: string): void {
    this.debugLog('🔴 Network lost:', { lastConnectionType: previousStatus.connectionType, timestamp });

    this.emitEvent({
      type: 'network_disconnected',
      data: {
        lastConnectionType: previousStatus.connectionType,
        timestamp
      }
    });
  }

  /**
   * Handle connection type changed
   */
  private handleConnectionTypeChanged(
    previousStatus: ConnectionStatus, 
    newStatus: ConnectionStatus, 
    timestamp: string
  ): void {
    this.debugLog('🔄 Connection type changed:', {
      from: previousStatus.connectionType,
      to: newStatus.connectionType,
      timestamp
    });

    this.emitEvent({
      type: 'connection_type_changed',
      data: {
        from: previousStatus.connectionType,
        to: newStatus.connectionType,
        timestamp
      }
    });
  }

  /**
   * Emit event to all listeners
   */
  private emitEvent(event: NetworkEvent): void {
    this.eventListeners.forEach(listener => {
      try {
        listener(event);
      } catch (error) {
        console.error('Error in network event listener:', error);
      }
    });
  }

  /**
   * Debug logging helper
   */
  private debugLog(message: string, data?: unknown): void {
    if (this.enableDebugLogging) {
      if (data) {
        console.log(message, data);
      } else {
        console.log(message);
      }
    }
  }

  /**
   * Get monitoring status
   */
  isCurrentlyMonitoring(): boolean {
    return this.isMonitoring;
  }

  /**
   * Force refresh network status
   */
  async refreshStatus(): Promise<ConnectionStatus> {
    try {
      this.currentStatus = await Network.getStatus();
      this.debugLog('🔄 Network status refreshed:', this.currentStatus);
      return this.currentStatus;
    } catch (error) {
      console.error('❌ Failed to refresh network status:', error);
      throw error;
    }
  }

  /**
   * Cleanup resources
   */
  cleanup(): void {
    this.stopMonitoring();
    this.removeAllEventListeners();
    this.currentStatus = null;
    
    this.debugLog('🧹 Network monitor cleaned up');
  }
}
